import axios from "axios";
import { toast } from "react-toastify";

// RIS (TeleRad / Softmed PACS) hand-off after an OPD invoice payment succeeds.
//
// The frontend never talks to technocratzindia directly: the Django backend's
// `telerad/patient_registration/` view fetches the PACS token, resolves TESTCODE
// from the medical_service UUID, stores a PatientDiacomTest row and forwards the
// DicomModInsertExternal call. This module only decides *when* to call it:
//   - facility has ris_integrated (from get_logged_in_user_info)
//   - the invoice line's service belongs to the "Radiology" service group
//   - no study (transaction_id) was already registered for that service today
//
// Payload follows the PACS format: dates YYYYMMDD, time HHMMSS, TESTCODE is the
// integer MedicalService id returned by get_medical_services/ (the UUID goes
// separately as medical_service_id for our own DICOM record).

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const RADIOLOGY_GROUP = "radiology";

const pad2 = (n) => String(n).padStart(2, "0");

const localDate = (d = new Date()) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const localTime = (d = new Date()) =>
  `${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;

// "2026-09-21" → "20260921" (PACS date format); "" stays "".
const compactDate = (iso) => String(iso || "").slice(0, 10).replace(/-/g, "");

const lineServiceId = (row) =>
  row?.items ?? row?.item_id ?? row?.medical_service_id ?? row?.service_id ?? null;

const isProductLine = (row) =>
  row?.category === "products" || row?.item_type === "Medical Product";

// "Male" / "Female" / "Other" (get_gender_display) or "M"/"F"/"O" → DICOM sex code.
const toPacsSex = (gender) => {
  const g = String(gender || "").trim().toUpperCase();
  if (!g) return "";
  return g[0] === "M" || g[0] === "F" ? g[0] : "O";
};

const asList = (res) =>
  Array.isArray(res?.data?.data)
    ? res.data.data
    : Array.isArray(res?.data?.service_list)
      ? res.data.service_list
      : [];

async function fetchRadiologyServices(serviceIds, headers) {
  const res = await axios.post(
    `${REACT_APP_BACKEND_ROUTE_URL}get_medical_services/`,
    { medical_service_ids: serviceIds },
    { headers },
  );
  return asList(res).filter(
    (s) => String(s?.service_group_name || "").trim().toLowerCase() === RADIOLOGY_GROUP,
  );
}

async function fetchPatient(patientId, headers) {
  const res = await axios.post(
    `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
    {
      filter_type: "patient_id",
      filter_details: { patient_id: patientId },
      multipleResults: true,
    },
    { headers },
  );
  const list = Array.isArray(res?.data?.patient_details) ? res.data.patient_details : [];
  return list[0] || null;
}

// medical_service_ids that already carry a StudyUID for this patient on `date`.
async function fetchRegisteredServiceIds(patientId, date, headers) {
  const res = await axios.get(
    `${REACT_APP_BACKEND_ROUTE_URL}telerad/patient_dicom_records/${patientId}/`,
    { headers },
  );
  const records = Array.isArray(res?.data?.data) ? res.data.data : [];
  return new Set(
    records
      .filter(
        (r) =>
          String(r?.transaction_id || "").trim() &&
          String(r?.registration_date || "").slice(0, 10) === date,
      )
      .map((r) => String(r.medical_service_id)),
  );
}

// DICOM modality code guessed from the service name; PACS accepts "" too.
const guessModality = (name) => {
  const n = String(name || "").toUpperCase();
  if (/\bMRI\b|\bMR\b/.test(n)) return "MR";
  if (/\bCT\b/.test(n)) return "CT";
  if (/X-?RAY|RADIOGRAPH/.test(n)) return "CR";
  if (/ULTRASO|\bUSG\b|SONOGRAPH|DOPPLER|\bECHO/.test(n)) return "US";
  if (/MAMMO/.test(n)) return "MG";
  if (/\bPET\b/.test(n)) return "PT";
  return "";
};

const postRegistration = (payload, headers) =>
  axios.post(`${REACT_APP_BACKEND_ROUTE_URL}telerad/patient_registration/`, payload, {
    headers,
  });

// PACS rejects studies for a TESTCODE it does not know. Register the test
// (DicomModTestInsertExternal via the backend) using the integer test_code the
// failed registration reported, so the caller can retry once.
async function registerTestWithPacs({ svc, testCode, userInfo, headers }) {
  const res = await axios.post(
    `${REACT_APP_BACKEND_ROUTE_URL}telerad/test_insert/`,
    {
      TESTCODE: String(testCode),
      TEST: svc.name || "",
      DEPT: svc.service_group_name || "Radiology",
      MODALITY: guessModality(svc.name),
      name: svc.name || "",
      description: svc.description || "",
      fee: svc.fee != null ? Number(svc.fee) : null,
      unit: svc.unit || "",
      active: true,
      expired: false,
      expiry_date: svc.expiry_date || null,
      deactivation_reason: null,
      snomed_code: svc.snomed_code || null,
      in_facility: true,
      hip_id: svc.hip_id || userInfo?.facility_id || "",
    },
    { headers },
  );
  return res?.data?.status === "Success";
}

/**
 * Fire-and-forget: registers every Radiology line of a just-paid OPD invoice
 * with the RIS. Never throws — failures surface as toasts only, so the payment
 * flow (receipt navigation etc.) is unaffected.
 *
 * @param {object} args
 * @param {object} args.invoice      invoice data (patient_id, referred_by, invoice_date, care_context_id)
 * @param {Array}  args.lineItems    invoice line items (any of the shapes used by InvoicePayment)
 * @param {object} args.userInfo     usePermissions().userInfo (needs ris_integrated)
 * @param {object} args.headers      request headers incl. Authorization
 */
export async function registerRadiologyStudiesAfterPayment({
  invoice,
  lineItems,
  userInfo,
  headers,
}) {
  try {
    if (!userInfo?.ris_integrated) return;

    const patientId = invoice?.patient_id;
    if (!patientId) return;

    const serviceLines = (Array.isArray(lineItems) ? lineItems : []).filter(
      (row) => !isProductLine(row) && lineServiceId(row),
    );
    const serviceIds = [...new Set(serviceLines.map((row) => String(lineServiceId(row))))];
    if (!serviceIds.length) return;

    const radiologyServices = await fetchRadiologyServices(serviceIds, headers);
    if (!radiologyServices.length) return;

    const now = new Date();
    const startDate = localDate(now);
    const startTime = localTime(now);

    const [patient, alreadyRegistered] = await Promise.all([
      fetchPatient(patientId, headers),
      fetchRegisteredServiceIds(patientId, startDate, headers),
    ]);

    if (!patient) {
      toast.warn("RIS: patient details not found, radiology order not sent.");
      return;
    }

    const referredBy = String(invoice?.referred_by || "").trim();
    const regDate = String(invoice?.invoice_date || invoice?.invoiceDate || "").slice(0, 10) || startDate;

    const pending = radiologyServices.filter(
      (svc) => !alreadyRegistered.has(String(svc.medical_service_id)),
    );
    if (!pending.length) return;

    const results = await Promise.all(
      pending.map(async (svc) => {
        const serviceId = String(svc.medical_service_id);
        const line = serviceLines.find((row) => String(lineServiceId(row)) === serviceId);
        const payload = {
          AET: process.env.REACT_APP_TELERAD_AET || "",
          STARTDATE: compactDate(startDate),
          STARTTIME: startTime,
          TESTCODE: svc.id != null ? String(svc.id) : serviceId,
          medical_service_id: serviceId,
          PERFPHYSNAME: "",
          DESCR: svc.name || line?.item_name || line?.itemName || "",
          REQPHYSICIAN: referredBy,
          REFPHYSNAME: referredBy,
          PATIENTID: patientId,
          PATIENTNAME: patient.name || invoice?.patient_name || invoice?.patientName || "",
          PATIENTBIRTHDATE: compactDate(patient.dob),
          PATIENTSEX: toPacsSex(patient.gender),
          PATIENTAGE: patient.age != null ? String(patient.age) : "",
          PATIENTREGDATE: compactDate(regDate),
          ISPAID: "1",
          PATIENTMOBILE: patient.phone_no || "",
          PATIENTEMAIL: patient.email || "",
          // main_patientdiacomtest has NOT NULL on both columns; the backend
          // inserts whatever it receives, so send "" rather than omitting them.
          appointment_id: line?.appointment_id ? String(line.appointment_id) : "",
          carecontext_id: invoice?.care_context_id ? String(invoice.care_context_id) : "",
        };

        try {
          let res = await postRegistration(payload, headers);
          if (res?.data?.status === "Success") return { ok: true, name: payload.DESCR };

          // Failed: add the test to PACS and retry once.
          console.warn("[RIS] patient_registration failed, response:", res?.data);
          const testCode = svc.id ?? res?.data?.test_code;
          if (testCode != null && testCode !== "") {
            const testAdded = await registerTestWithPacs({ svc, testCode, userInfo, headers });
            if (testAdded) {
              res = await postRegistration(payload, headers);
              if (res?.data?.status === "Success") return { ok: true, name: payload.DESCR };
            }
          }
          return { ok: false, name: payload.DESCR, message: res?.data?.message };
        } catch (err) {
          return {
            ok: false,
            name: payload.DESCR,
            message: err?.response?.data?.message || err?.message,
          };
        }
      }),
    );

    const sent = results.filter((r) => r.ok);
    const failed = results.filter((r) => !r.ok);
    if (sent.length) {
      toast.success(`Radiology order sent to RIS: ${sent.map((r) => r.name).join(", ")}`);
    }
    failed.forEach((r) => {
      console.error("RIS registration failed:", r.name, r.message);
      toast.warn(`RIS registration failed for ${r.name}${r.message ? `: ${r.message}` : ""}`);
    });
  } catch (err) {
    console.error("RIS integration error:", err);
    toast.warn("Could not send radiology order to RIS.");
  }
}
